/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/contexts/AdminAuthContext.tsx
 * DESCRIPTION: Admin authorization context using Clerk user metadata.
 *              Determines admin access based on user.publicMetadata.role
 *              or user.unsafeMetadata.role === 'admin'.
 *
 * AUTHORIZATION MODEL:
 * - Admin access is granted ONLY if Clerk user metadata contains:
 *   - publicMetadata.role === 'admin' (preferred, backend-writable)
 *   - OR unsafeMetadata.role === 'admin' (fallback, user-writable)
 * - No password required; access is tied to Clerk user identity
 * - Admin status is read-only from frontend perspective
 *
 * MIGRATION:
 * v1.0.0 (2026-03-27): Temporary password-based admin auth
 * v2.0.0 (2026-09-23): Clerk metadata-based admin authorization
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2026-03-27
 * @updated 2026-09-23 - Replaced password-based auth with Clerk metadata
 *                       authorization (publicMetadata.role === 'admin')
 * ─────────────────────────────────────────────────────────────────────
 */

import { createContext, useContext, useEffect, useState } from 'react';
import { useUser } from '@clerk/react';

interface AdminAuthContextType {
  isAdminAuthenticated: boolean;
  isAdminLoading: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

/**
 * AdminAuthProvider Component
 * Provides admin authorization state based on Clerk user metadata.
 *
 * @param children - Child components to wrap with admin context
 */
export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!isLoaded || !user) {
      setIsAdmin(false);
      return;
    }

    // Check admin status from Clerk metadata
    // Priority: publicMetadata (backend-writable) > unsafeMetadata (user-writable)
    const publicMeta = (user.publicMetadata as any) || {};
    const unsafeMeta = (user.unsafeMetadata as any) || {};
    
    const role = publicMeta.role || unsafeMeta.role;
    setIsAdmin(role === 'admin');
  }, [user, isLoaded]);

  const value: AdminAuthContextType = {
    isAdminAuthenticated: isAdmin,
    isAdminLoading: !isLoaded,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

/**
 * useAdminAuth Hook
 * Returns admin authorization state from context.
 *
 * @returns {AdminAuthContextType} Admin auth state
 */
export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};